import { canSee, navFor, pathForView, viewForPath, viewFromDesign, type Role, type ViewId } from '@aithora/shared';
import type { GuideData, GuideGo, GuideRole, Tutorial } from '../../content/guideData';
import { SECTION_SLUG, type SectionName } from '../settings/lens';

/**
 * The Guide's values, ported from the design's Guide page (Aithora Guide.dc.html,
 * renderVals). Pure, so they are tested alone. The one change from the design: the role
 * comes from the signed-in session, never from the URL, and an answer's deep link is a
 * console route (never a role).
 */

/** The guide a stored role reads, by the design's name for it. */
export function guideRoleFor(role: Role | null): string {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Admin';
    case 'DEVELOPER':
      return 'Dev';
    case 'SALES':
      return 'Sales';
    case 'ADMIN':
      return 'Facility Admin';
    case 'CAREGIVER':
    case 'STAFF':
      return 'Care Staff';
    case 'FAMILY_MEMBER':
      return 'Family';
    case 'RESIDENT':
      return 'Resident';
    case 'MEMBER':
      return 'Aithora Home';
    default:
      // Operator, and Partner (who reads the Operator's guide, read-only)
      return 'Operator';
  }
}

/** Only Super Admin and Dev see the role picker and may open another role's guide. */
export function mayPickGuide(role: Role | null): boolean {
  return role === 'SUPER_ADMIN' || role === 'DEVELOPER';
}

/** Every guide name the picker offers, in the design's order (roles, then aliases). */
export function guideNames(G: GuideData): string[] {
  return [...Object.keys(G.roles), ...Object.keys(G.alias)];
}

export function guideOf(G: GuideData, raw: string): { raw: string; name: string; R: GuideRole | null } {
  const name = G.alias[raw] || raw;
  return { raw, name, R: G.roles[name] ?? null };
}

// The design's state keys that pick a tab on the page they open.
const TAB_KEYS = ['atTab', 'seSec', 'pcTab', 'monTab', 'capTab', 'slTab', 'crmTop'];

/**
 * A design deep link ({view, pcTab: 'History'}) as the console route it means, or null
 * when the page does not exist in the console or the role may not open it.
 */
export function consoleHref(go: GuideGo | null | undefined, role: Role | null): string | null {
  if (!go) return null;
  const view = viewFromDesign(go.view);
  if (!view) return null;
  const open = canSee(role, view) || (view === 'companion' && (role === 'MEMBER' || role === 'FAMILY_MEMBER'));
  if (!open) return null;
  const tabKey = TAB_KEYS.find((k) => k !== 'crmTop' && go[k]);
  if (!tabKey) return pathForView(view);
  const name = go[tabKey];
  // Each page reads its own parameter: Settings picks a section by ?section=<slug>
  // (settings/SettingsPage.tsx), Oversight reads ?tab=summary|live|ledger
  // (oversight/OversightPage.tsx), and Capture reads none (it opens on Snapshot).
  if (tabKey === 'seSec') return `${pathForView(view)}?section=${SECTION_SLUG(name as SectionName)}`;
  if (tabKey === 'monTab') {
    const t = OVERSIGHT_TAB[name];
    return t ? `${pathForView(view)}?tab=${t}` : pathForView(view);
  }
  if (tabKey === 'capTab') return pathForView(view);
  return `${pathForView(view)}?tab=${encodeURIComponent(name)}`;
}

const OVERSIGHT_TAB: Record<string, string> = { Summary: 'summary', 'Live Decisions': 'live', 'Truth Ledger': 'ledger' };

export interface GuidePair {
  k: string;
  v: string;
}
export interface GuideScreen {
  title: string;
  lead: string;
  body: string;
  pairs: GuidePair[];
  points: string[];
  note: string;
  /** Console route for "Open in console", '' when none. */
  go: string;
}
export interface GuideHow {
  i: number;
  q: string;
  a: string;
  href: string;
  group: string;
}

const GROUP: Record<string, string> = {
  worklist: 'Worklist', guardian: 'Guardian', atlas: 'Atlas', overview: 'Overview', settings: 'Settings', residents: 'Residents', policy: 'Policy',
  monitoring: 'Oversight', pulse: 'System Pulse', scorecard: 'Performance', capture: 'Capture', sales: 'Sales', family: 'Family Portal', companion: 'Atlas', home: 'Aithora Home',
};

function pairsOf(s: Tutorial['screens'][number]): GuidePair[] {
  const out: GuidePair[] = [];
  (['promise', 'steps', 'login', 'badges', 'rows'] as const).forEach((k) => (s[k] || []).forEach((r) => out.push({ k: r[0], v: r[1] })));
  (s.list || []).forEach((r) => out.push({ k: r[0], v: `${r[1]} · ${r[2]}` }));
  (s.compare || []).forEach((r) => out.push({ k: r[0], v: r[1] }));
  return out;
}

/** The First 10 minutes (from the role's tutorial) or the setup steps, and its end card. */
export function guideScreens(R: GuideRole, tutorials: Record<string, Tutorial>, role: Role | null): { screens: GuideScreen[]; end: Tutorial['end'] | null } {
  const T = R.tutorial ? tutorials[R.tutorial] : undefined;
  if (T) {
    return {
      end: T.end ?? null,
      screens: T.screens.map((s) => ({
        title: s.title, lead: s.lead || '', body: s.body || '', pairs: pairsOf(s),
        points: [...(s.points || []), ...(s.tabs ? [`Tabs: ${s.tabs.join(', ')}`] : [])],
        note: [s.note, s.why].filter(Boolean).join(' '), go: '',
      })),
    };
  }
  if (R.first) return { end: null, screens: R.first.map((f) => ({ title: f[0], lead: '', body: f[1], pairs: [], points: [], note: '', go: consoleHref(f[2], role) ?? '' })) };
  return { end: null, screens: [] };
}

export function guideHows(R: GuideRole, role: Role | null): GuideHow[] {
  return R.howto.map((h, i) => ({ i, q: h[0], a: h[1], href: consoleHref(h[2], role) ?? '', group: h[2] ? GROUP[h[2].view] || 'Console' : 'Anywhere' }));
}

/** A picker card (design card()): the steps and answers are counted from the data. */
export function pickerCard(G: GuideData, tutorials: Record<string, Tutorial>, r: string): { label: string; lead: string; meta: string } {
  const R0 = G.roles[G.alias[r] || r];
  const T0 = R0?.tutorial ? tutorials[R0.tutorial] : undefined;
  const steps = T0 ? T0.screens.length : (R0?.first || []).length;
  const hw = (R0?.howto || []).length;
  const LEAD: Record<string, string> = { 'Super Admin': 'Everything Operator sees, plus who can do what.', Dev: 'Everything Operator sees, for building and testing.' };
  return { label: r, lead: LEAD[r] || R0?.lead || '', meta: `${steps ? `${steps} steps · ` : ''}${hw} answers${R0?.manual ? ' · manual' : ''}` };
}

export interface CrossHit {
  q: string;
  a: string;
  who: string;
  /** A console route, or that role's guide when the answer opens no page this role can. */
  href: string;
}
/** The picker's search across every role's answers: every word must match (design pq). */
export function crossRoleSearch(G: GuideData, query: string, role: Role | null): CrossHit[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter((w) => w.length > 1);
  const hits: CrossHit[] = [];
  if (!words.length) return hits;
  Object.keys(G.roles).forEach((rn) =>
    (G.roles[rn].howto || []).forEach((h) => {
      const t = `${h[0]} ${h[1]}`.toLowerCase();
      if (words.every((w) => t.includes(w))) hits.push({ q: h[0], a: h[1], who: rn, href: consoleHref(h[2], role) ?? `/guide?for=${encodeURIComponent(rn)}` });
    }),
  );
  return hits;
}

/** A stable URL name for a step or an answer (?topic=). */
export function topicSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export interface GuideSearchHit {
  k: 's' | 'h';
  i: number;
  tag: 'Step' | 'How';
  title: string;
  where: string;
}
/** The staff page's search over its own steps and answers (design res). */
export function guideSearch(screens: GuideScreen[], hows: GuideHow[], query: string): GuideSearchHit[] {
  const qq = query.trim().toLowerCase();
  if (!qq) return [];
  const hit = (...xs: string[]) => qq.split(/\s+/).every((wd) => xs.join(' ').toLowerCase().includes(wd));
  const res: GuideSearchHit[] = [];
  screens.forEach((s, i) => {
    if (hit(s.title, s.lead, s.body, s.note, s.points.join(' '), s.pairs.map((p) => `${p.k} ${p.v}`).join(' '))) res.push({ k: 's', i, tag: 'Step', title: s.title, where: `Step ${i + 1}` });
  });
  hows.forEach((h) => {
    if (hit(h.q, h.a, h.group)) res.push({ k: 'h', i: h.i, tag: 'How', title: h.q, where: h.group });
  });
  return res.slice(0, 8);
}

/** Words that say nothing about what the reader wants ("what does pulse do" is about pulse). */
const STOP = new Set(
  'a an and are can could do does for from get how i in is it me my of on or see show the this to use what when where which who why will with you your'.split(' '),
);

/**
 * The closest steps and answers when no entry matches every word (guideSearch): each one
 * scores by how many of the question's meaningful words it holds (a word in the title
 * counts double); best first.
 */
export function guideLooseSearch(screens: GuideScreen[], hows: GuideHow[], query: string, max = 5): GuideSearchHit[] {
  const words = [...new Set(query.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 1 && !STOP.has(w)))];
  if (!words.length) return [];
  const score = (title: string, rest: string) => {
    const t = title.toLowerCase();
    const r = rest.toLowerCase();
    return words.reduce((n, w) => n + (t.includes(w) ? 2 : r.includes(w) ? 1 : 0), 0);
  };
  const scored: { n: number; hit: GuideSearchHit }[] = [];
  screens.forEach((s, i) => {
    const n = score(s.title, [s.lead, s.body, s.note, s.points.join(' '), s.pairs.map((p) => `${p.k} ${p.v}`).join(' ')].join(' '));
    if (n) scored.push({ n, hit: { k: 's', i, tag: 'Step', title: s.title, where: `Step ${i + 1}` } });
  });
  hows.forEach((h) => {
    const n = score(h.q, `${h.a} ${h.group}`);
    if (n) scored.push({ n, hit: { k: 'h', i: h.i, tag: 'How', title: h.q, where: h.group } });
  });
  return scored
    .sort((a, b) => b.n - a.n)
    .slice(0, max)
    .map((x) => x.hit);
}

/** The menu names this role may open, each once. */
const menuFor = (role: Role | null) => navFor(role).filter((n, i, a) => a.findIndex((x) => x.label === n.label) === i);

/**
 * The question the Guide sends to Atlas (POST /api/atlas/ask, the same governed route the
 * Atlas drawer uses) when its own search has no match: the reader's words, framed so Atlas
 * answers as product help and names the page. Only pages this role may open are offered.
 */
export function helpQuestion(query: string, guide: string, role: Role | null): string {
  const pages = menuFor(role).map((n) => n.label);
  return [
    `Product help, asked on the ${guide} guide. Answer as Aithora product help, not about any resident or facility record.`,
    'In two or three short sentences say what the feature does and how to use it, then name the console page to open by its menu name' +
      (pages.length ? ` (one of: ${pages.join(', ')}).` : '.'),
    `Question: ${query.trim()}`,
  ].join(' ');
}

/** Short names people use for a page the menu words differently. */
const PAGE_ALIAS: [RegExp, ViewId][] = [[/\bpulse\b/i, 'pulse']];

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The console pages an answer points to that this role may open: the links Atlas sent,
 * then every menu name in its words in the order they appear (longest name first, so
 * "Atlas Intelligence" is never also read as "Atlas"). Each page once.
 */
export function helpPages(text: string, links: { label: string; path: string }[], role: Role | null): { label: string; path: string }[] {
  const out: { label: string; path: string }[] = [];
  const add = (label: string, path: string) => {
    const view = viewForPath(path);
    if (!view || !canSee(role, view) || out.some((o) => o.path === path)) return;
    out.push({ label, path });
  };
  for (const l of links) add(l.label, l.path);
  const menu = menuFor(role);
  let rest = text;
  const found: { at: number; label: string; path: string }[] = [];
  for (const n of [...menu].sort((a, b) => b.label.length - a.label.length)) {
    const m = new RegExp(`\\b${escapeRe(n.label)}\\b`, 'i').exec(rest);
    if (!m) continue;
    found.push({ at: m.index, label: n.label, path: n.path });
    rest = rest.slice(0, m.index) + ' '.repeat(m[0].length) + rest.slice(m.index + m[0].length);
  }
  for (const [re, view] of PAGE_ALIAS) {
    const m = re.exec(rest);
    const item = menu.find((n) => n.view === view);
    if (m && item) found.push({ at: m.index, label: item.label, path: item.path });
  }
  found.sort((a, b) => a.at - b.at).forEach((f) => add(f.label, f.path));
  return out;
}
